/**
 * Alert-type contact-rule (notification tree) API service
 * (ADR-141 §2/§4).
 *
 * Backed by the append-only rules endpoints:
 *   GET/POST  /api/v2/alert-type/{alert_type_id}/contact-rules/
 *   DELETE    /api/v2/alert-type/{alert_type_id}/contact-rules/{rule_id}/
 *
 * Append-only semantics (rubric discipline, same as ADR-116 cells):
 * POST for the same (contact | tag) target CLOSES the previous active
 * row server-side and returns a brand-new id; DELETE closes a row.
 * Consumers must NEVER cache rule ids — every write invalidates the
 * per-type key root and refetches (see `useContactRules.ts`).
 */

import type { components } from '@tiaki/http-client/types';
import { client } from '../lib/http-client';

export type AlertTypeContactRule =
  components['schemas']['AlertTypeContactRule'];
export type AlertTypeContactRuleWritePayload =
  components['schemas']['AlertTypeContactRuleRequest'];

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

/** List the ACTIVE tree rows for an alert type (closed history excluded). */
export async function getContactRules(
  alertTypeId: number,
): Promise<AlertTypeContactRule[]> {
  const result = await client.GET(
    '/api/v2/alert-type/{alert_type_id}/contact-rules/',
    { params: { path: { alert_type_id: alertTypeId } } },
  );
  return unwrap(result);
}

/**
 * Append a tree row. If an active row already exists for the same
 * (contact | tag) target the server closes it and inserts a fresh row —
 * the returned rule carries a NEW id.
 */
export async function createContactRule(
  alertTypeId: number,
  body: AlertTypeContactRuleWritePayload,
): Promise<AlertTypeContactRule> {
  const result = await client.POST(
    '/api/v2/alert-type/{alert_type_id}/contact-rules/',
    { params: { path: { alert_type_id: alertTypeId } }, body },
  );
  return unwrap(result);
}

/** Close (retire) an active tree row. History is never destroyed. */
export async function deleteContactRule(
  alertTypeId: number,
  ruleId: number,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/alert-type/{alert_type_id}/contact-rules/{rule_id}/',
    { params: { path: { alert_type_id: alertTypeId, rule_id: ruleId } } },
  );
  unwrap(result);
}
